"use client";

import type { DbSetReview, ProcessedSet, Review } from "@workspace/types";
import {
	getB2BDisplayParts,
	isProgramSet,
	setMatchesSearch,
} from "@workspace/types";

import {
	type ChangeEvent,
	memo,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	BookmarkFilter,
	type BookmarkFilters,
} from "@/components/shared/filters/bookmark/bookmark-filter";
import { GenreFilter } from "@/components/shared/filters/genre/genre-filter";
import {
	ARTIST_SORT_OPTIONS,
	UniversalSortDropdown,
} from "@/components/shared/filters/search/universal-sort-dropdown";
import { EnhancedSearchInput } from "@/components/shared/filters/search/enhanced-search-input";
import { RealtimeSetCard } from "@/components/shared/realtime-set-card";
import { useArtistGenreData } from "@/hooks/features/artist/ui";
import { useResponsiveFestivalArtistLimit } from "@/hooks/shared/use-responsive-limit";
import { applyGenreFilterToSets, matchesSearch } from "@workspace/shared-utils";
import { ArtistsEmptyState } from "./artists/artists-empty-state";
import {
	shouldShowInternalControls,
	type TabVariant,
	useTabVariant,
} from "./use-tab-variant";

// Props interface that supports both personal and public views
interface ArtistsTabProps {
	// Core data
	processedSets: ProcessedSet[];
	festivalSlug?: string;

	// Variant configuration
	variant: TabVariant;

	// Authentication/ownership (personal uses isOwnProfile, public uses isAuthenticated)
	authState: boolean;

	// Reviews data - supports both formats
	setReviews?: DbSetReview[]; // Personal view format
	reviews?: Review[]; // Public view simplified format

	// Schedule data
	savedSchedule: string[];

	// Event handlers
	onStartReview: (setId: string) => void;
	onToggleSetInSchedule: (setId: string) => void;
	onAuthRequired?: (message?: string) => void; // Public view only

	// Filter state and handlers
	// Personal view filters
	bookmarkFilters?: BookmarkFilters;
	onBookmarkFiltersChange?: (filters: BookmarkFilters) => void;

	// Public view filters
	showOnlySavedArtists?: boolean;

	// Shared filter controls
	searchTerm?: string;
	selectedGenre?: string;
	sortBy?: string;
	onSearchChange?: (e: ChangeEvent<HTMLInputElement>) => void;
	onGenreChange?: (genre: string) => void;
	onSortChange?: (sort: string) => void;

	// Loading states
	isLoadingInitial?: boolean;
	isSearching?: boolean; // Track search state
}

export const ArtistsTab = memo(function ArtistsTab({
	processedSets,
	festivalSlug,
	variant,
	authState,
	setReviews,
	reviews,
	savedSchedule,
	onStartReview,
	onToggleSetInSchedule,
	onAuthRequired,
	bookmarkFilters = {
		saved: false,
		rated: false,
		following: false,
		stream: false,
	},
	onBookmarkFiltersChange,
	showOnlySavedArtists = false,
	searchTerm = "",
	selectedGenre = "all",
	sortBy = "popularity",
	onSearchChange,
	onGenreChange,
	onSortChange,
	isLoadingInitial = false,
	isSearching = false,
}: ArtistsTabProps) {
	const features = useTabVariant(variant);

	// Internal state for when not controlled externally
	const [internalSearchTerm, setInternalSearchTerm] = useState("");
	const [internalSelectedGenre, setInternalSelectedGenre] = useState("all");
	const [internalSortBy, setInternalSortBy] = useState("popularity");

	const actualSearchTerm =
		searchTerm !== undefined ? searchTerm : internalSearchTerm;
	const actualSelectedGenre =
		selectedGenre !== undefined ? selectedGenre : internalSelectedGenre;
	const actualSortBy = sortBy !== undefined ? sortBy : internalSortBy;
	const showInternalControls = shouldShowInternalControls(
		onSearchChange,
		onGenreChange,
	);

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (onSearchChange) {
			onSearchChange(e);
		} else {
			setInternalSearchTerm(e.target.value);
		}
	};

	const handleSearchValueChange = (value: string) => {
		const fakeEvent = {
			target: { value },
		} as ChangeEvent<HTMLInputElement>;
		handleSearchChange(fakeEvent);
	};

	const handleGenreChange = (genre: string) => {
		if (onGenreChange) {
			onGenreChange(genre);
		} else {
			setInternalSelectedGenre(genre);
		}
	};

	const handleSortChange = (sort: string) => {
		if (onSortChange) {
			onSortChange(sort);
		} else {
			setInternalSortBy(sort);
		}
	};

	// Filter logic
	const filteredArtists = useMemo(() => {
		// Client-side filtering
		const afterProgramFilter = processedSets.filter(
			(set) => !isProgramSet(set),
		);

		// Apply search filter
		const afterSearchFilter = afterProgramFilter.filter((set) => {
			if (actualSearchTerm.trim()) {
				return setMatchesSearch(set, actualSearchTerm, matchesSearch);
			}
			return true;
		});

		// Apply genre filter using the utility function
		const afterGenreFilter = applyGenreFilterToSets(
			afterSearchFilter,
			actualSelectedGenre === "all" ? null : actualSelectedGenre,
		);

		// Apply sorting
		const sorted = afterGenreFilter.sort((a, b) => {
			// Check if sets are unrevealed surprises (no artist_id)
			const isUnrevealedA = a.is_surprise && !a.artist;
			const isUnrevealedB = b.is_surprise && !b.artist;

			// If one is unrevealed and the other isn't, put unrevealed at the end
			if (isUnrevealedA && !isUnrevealedB) return 1;
			if (!isUnrevealedA && isUnrevealedB) return -1;

			// Helper function to get name for sorting
			const getNameForSorting = (set: ProcessedSet) => {
				// For surprise sets (revealed or not), check if they have an artist
				if (set.is_surprise) {
					if (set.artist) {
						// Revealed surprise - use actual artist name for sorting
						return set.artist.name;
					}
					// Unrevealed surprise - use placeholder
					return set.placeholder_name || "?????";
				}

				// For non-surprise sets, use display logic
				// This handles B2B, aliases, etc. properly
				const displayName = getB2BDisplayParts(set).mainText || "";

				// If display name is empty (data issue), fall back to artist name
				if (!displayName && set.artist) {
					return set.artist.name;
				}

				return displayName;
			};

			// Sort based on selected option
			if (actualSortBy === "popularity") {
				// Sort by popularity (most popular first)
				// Use spotify_popularity if available, otherwise fall back to alphabetical
				const popA = a.artist?.spotify_popularity ?? 0;
				const popB = b.artist?.spotify_popularity ?? 0;

				if (popA !== popB) {
					return popB - popA; // Higher popularity first
				}
				// If same popularity, fall through to alphabetical
			}

			// For alpha and alpha-desc, or as fallback for popularity ties
			const nameA = getNameForSorting(a);
			const nameB = getNameForSorting(b);

			// Use en-US locale for consistent sorting across browsers
			// Also use sensitivity: 'base' to ignore case and accents
			const comparison = nameA.localeCompare(nameB, "en-US", {
				sensitivity: "base",
				numeric: true,
			});

			// Reverse for Z to A
			return actualSortBy === "alpha-desc" ? -comparison : comparison;
		});

		return sorted;
	}, [processedSets, actualSearchTerm, actualSelectedGenre, actualSortBy]);

	// Calculate available genres
	// Use extracted hook for genre data
	const { allGenres: availableGenres, hasNoGenreArtists } =
		useArtistGenreData(processedSets);

	// Apply filters based on variant
	const applyVariantFilters = (sets: ProcessedSet[]) => {
		if (variant === "personal" && features.showBookmarkFilters) {
			// Personal view: Apply bookmark filters
			const { saved, rated, stream } = bookmarkFilters;

			if (!saved && !rated && !stream) return sets;

			const filtered = sets.filter((set) => {
				// Use reviews prop instead of setReviews for personal view
				const review = reviews?.find((r) => r.setId === set.id);
				const isInSchedule = savedSchedule.includes(set.id);

				const matchesSaved =
					!saved ||
					isInSchedule ||
					review?.wantToSee ||
					review?.mustSee ||
					(review?.rating || 0) > 0 ||
					review?.loved ||
					(review?.content && review.content.trim().length > 0);
				const matchesRated =
					!rated ||
					(review?.rating || 0) > 0 ||
					review?.loved ||
					(review?.content && review.content.trim().length > 0);
				const matchesStream = !stream || !!set.youtube_url;

				const passes = matchesSaved && matchesRated && matchesStream;
				return passes;
			});

			return filtered;
		}
		if (variant === "public") {
			// Public view: Apply bookmark filters when provided, otherwise simple saved filter
			if (bookmarkFilters && onBookmarkFiltersChange) {
				const { saved, rated, stream } = bookmarkFilters;

				// If no filters are active, return all sets
				if (!saved && !rated && !stream) return sets;

				const filtered = sets.filter((set) => {
					const review = reviews?.find((r) => r.setId === set.id);
					const isInSchedule = savedSchedule.includes(set.id);

					const matchesSaved =
						!saved ||
						isInSchedule ||
						!!review?.rating ||
						!!review?.loved ||
						!!review?.content;
					const matchesRated =
						!rated || !!review?.rating || !!review?.loved || !!review?.content;
					const matchesStream = !stream || !!set.youtube_url;

					const passes = matchesSaved && matchesRated && matchesStream;
					return passes;
				});

				return filtered;
			}
			if (showOnlySavedArtists) {
				// Fallback: Simple saved filter
				return sets.filter((set) => savedSchedule.includes(set.id));
			}
		}

		return sets;
	};

	const artistsData = applyVariantFilters(filteredArtists);

	// Convert reviews to the format expected by SetCard
	// Returns null (not undefined) when reviews are batched but no review exists for this set
	const getReviewForCard = useCallback(
		(setId: string): Review | null | undefined => {
			if (variant === "personal") {
				// For personal view, reviews are already in camelCase format from UsernameFestivalPageClient
				if (reviews) {
					return reviews.find((r) => r.setId === setId) ?? null;
				}
				// Fallback to setReviews if reviews not provided (shouldn't happen)
				if (setReviews) {
					const review = setReviews.find(
						(r) => r.set_id === setId || r.program_set_id === setId,
					);
					return review
						? {
								setId: review.set_id || review.program_set_id || setId,
								artistId: review.artist_id,
								rating: review.rating,
								content: review.content || "",
								loved: review.loved,
								sawLive: review.saw_live,
								sawStream: review.saw_stream,
								wantToSee: review.want_to_see,
								mustSee: review.must_see,
							}
						: null;
				}
			}
			if (variant === "public" && reviews) {
				return reviews.find((r) => r.setId === setId) ?? null;
			}
			return undefined;
		},
		[variant, setReviews, reviews],
	);

	const readOnly = features.readOnlyCondition(authState);

	// Get responsive skeleton counts
	const { initialLimit: skeletonCount } = useResponsiveFestivalArtistLimit();

	// Track if we've ever received data
	const hasReceivedData = useRef(false);
	useEffect(() => {
		if (processedSets.length > 0) {
			hasReceivedData.current = true;
		}
	}, [processedSets.length]);

	// Determine if we should show loading state
	// Show loading if:
	// 1. Explicitly told we're loading (isLoadingInitial === true)
	// 2. We've never received any data yet (!hasReceivedData.current) AND processedSets is empty
	// Note: Don't show loading if we've received data before (prevents infinite skeleton on event type switch)
	const shouldShowLoading =
		isLoadingInitial === true &&
		(!hasReceivedData.current || processedSets.length > 0);

	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				{/* Show controls skeleton if they would be visible */}
				{showInternalControls && (
					<div>
						<div className="flex gap-3">
							<div className="h-10 flex-1 animate-pulse rounded-md bg-muted" />
							<div className="h-10 flex-1 animate-pulse rounded-md bg-muted" />
						</div>
						<div className="mt-4 flex items-center justify-between">
							<div className="h-10 flex-1 animate-pulse rounded-md bg-muted" />
							{features.showBookmarkFilters && onBookmarkFiltersChange && (
								<div className="ml-2 h-10 w-10 animate-pulse rounded-md bg-muted" />
							)}
						</div>
					</div>
				)}
				{/* Artists grid skeleton - responsive count */}
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
					{[...Array(skeletonCount)].map((_, i) => (
						<div
							key={`skeleton-${i}`}
							className="aspect-[3/4] animate-pulse rounded-lg bg-muted"
						/>
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Controls - only show if not controlled by parent */}
			{showInternalControls && (
				<div>
					<div className="flex gap-3">
						<div className="flex-1">
							<GenreFilter
								value={actualSelectedGenre}
								onValueChange={handleGenreChange}
								availableGenres={availableGenres}
								hasNoGenreArtists={hasNoGenreArtists}
								placeholder="All Genres"
							/>
						</div>
						<div className="flex-1">
							<UniversalSortDropdown
								value={actualSortBy}
								onValueChange={handleSortChange}
								options={ARTIST_SORT_OPTIONS}
								preserveScrollPosition={true}
							/>
						</div>
					</div>

					<div className="mt-4 flex items-center justify-between">
						<EnhancedSearchInput
							placeholder="Search artists..."
							className="flex-1"
							value={actualSearchTerm}
							onValueChange={handleSearchValueChange}
							showLoadingIndicator={true}
							isLoading={isSearching}
						/>
						<div className="flex flex-row gap-2 pl-2">
							{features.showBookmarkFilters && onBookmarkFiltersChange && (
								<BookmarkFilter
									filters={bookmarkFilters}
									onFiltersChange={onBookmarkFiltersChange}
									title="Filter artists"
								/>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Artists Grid */}
			{artistsData.length === 0 && !isSearching && !isLoadingInitial ? (
				<ArtistsEmptyState
					variant={variant}
					showOnlySavedArtists={showOnlySavedArtists}
					authState={authState}
					actualSearchTerm={actualSearchTerm}
					actualSelectedGenre={actualSelectedGenre}
					bookmarkFilters={bookmarkFilters}
					onAuthRequired={onAuthRequired}
					onBookmarkFiltersChange={onBookmarkFiltersChange}
					handleGenreChange={handleGenreChange}
					handleSearchChange={handleSearchChange}
				/>
			) : (
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
					{artistsData.map((set) => {
						const review = getReviewForCard(set.id);

						return (
							<Suspense
								key={set.id}
								fallback={
									<div className="aspect-[3/4] animate-pulse rounded-lg bg-muted" />
								}
							>
								<RealtimeSetCard
									set={set}
									review={review}
									savedSchedule={savedSchedule}
									festivalSlug={festivalSlug}
									variant={variant}
									onStartReview={onStartReview}
									onToggleSetInSchedule={onToggleSetInSchedule}
									readOnly={readOnly}
									onAuthRequired={onAuthRequired}
									layout="vertical"
								/>
							</Suspense>
						);
					})}
				</div>
			)}
		</div>
	);
});
